import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { FaStethoscope, FaUserMd, FaMobileAlt, FaChartLine, FaSearch, FaStar, FaVideo, FaEnvelope, FaComments, FaCalendarCheck, FaMapMarkerAlt, FaNewspaper, FaShieldAlt } from 'react-icons/fa';
import Home from '../Home';

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    font-family: 'Arial', sans-serif;
  }
  
  body {
    background-color: #f8f9fa;
    line-height: 1.6;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Header = styled.header`
  background: linear-gradient(135deg, #1a73e8 0%, #6c63ff 100%);
  color: white;
  padding: 80px 0;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const HeaderTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 20px;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeaderSubtitle = styled.p`
  font-size: 1.2rem;
  max-width: 600px;
  margin: 0 auto;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 20px;
  }
`;

const Section = styled.section`
  padding: 80px 0;
  
  &:nth-child(even) {
    background-color: white;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  color: #2c3e50;
  margin-bottom: 50px;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 30px;
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 50px;
`;

const BenefitCard = styled.div`
  background: white;
  padding: 40px 30px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  text-align: center;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const BenefitIcon = styled.div`
  font-size: 3rem;
  color: #1a73e8;
  margin-bottom: 20px;
`;

const BenefitTitle = styled.h3`
  font-size: 1.5rem;
  color: #2c3e50;
  margin-bottom: 15px;
`;

const BenefitDescription = styled.p`
  color: #666;
  line-height: 1.6;
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  margin-top: 50px;
`;

const StatCard = styled.div`
  text-align: center;
  padding: 30px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
`;

const StatNumber = styled.div`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 10px;
`;

const StatLabel = styled.div`
  font-size: 1.2rem;
  opacity: 0.9;
`;

const PricingSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 50px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PricingCard = styled.div`
  background: white;
  border-radius: 15px;
  padding: 40px 30px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  text-align: center;
  position: relative;
  transition: transform 0.3s ease;
  border: ${props => props.featured ? '3px solid #1a73e8' : '1px solid #e0e0e0'};
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  background: #1a73e8;
  color: white;
  padding: 5px 20px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
`;

const PlanName = styled.h3`
  font-size: 1.8rem;
  color: #2c3e50;
  margin-bottom: 10px;
`;

const PlanPrice = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: #1a73e8;
  margin: 20px 0;
  
  span {
    font-size: 1rem;
    color: #666;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  margin: 30px 0;
  text-align: left;
`;

const FeatureItem = styled.li`
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  color: #666;
  
  &:before {
    content: '✓';
    color: #4caf50;
    font-weight: bold;
    margin-right: 10px;
  }
`;

const CTAButton = styled.button`
  background: ${props => props.primary ? '#1a73e8' : 'transparent'};
  color: ${props => props.primary ? 'white' : '#1a73e8'};
  border: 2px solid #1a73e8;
  padding: 15px 30px;
  border-radius: 30px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  
  &:hover {
    background: ${props => props.primary ? '#0d62d9' : '#1a73e8'};
    color: white;
  }
`;

const TestimonialSection = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 60px 0;
  text-align: center;
`;

const Testimonial = styled.div`
  max-width: 800px;
  margin: 0 auto;
  font-style: italic;
  font-size: 1.2rem;
  line-height: 1.8;
`;

const DoctorName = styled.div`
  margin-top: 20px;
  font-weight: 600;
  font-size: 1.1rem;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-top: 50px;
`;

const ServiceCard = styled.div`
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  display: flex;
  align-items: flex-start;
  gap: 20px;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const ServiceIcon = styled.div`
  font-size: 2.5rem;
  color: #1a73e8;
  min-width: 60px;
`;

const ServiceContent = styled.div`
  flex: 1;
`;

const ServiceTitle = styled.h3`
  font-size: 1.4rem;
  color: #2c3e50;
  margin-bottom: 10px;
`;

const ServiceDescription = styled.p`
  color: #666;
  line-height: 1.6;
`;

const Footer = styled.footer`
  background: #2c3e50;
  color: white;
  text-align: center;
  padding: 40px 0;
`;

const DigitalMarketing = () => {
  return (
    <Home>
      <GlobalStyle />
      
      <Header>
        <Container>
          <HeaderTitle>Digital Marketing for Healthcare Professionals</HeaderTitle>
          <HeaderSubtitle>
            Boost your medical practice with proven digital strategies. Attract more patients, 
            build trust, and grow your healthcare business online.
          </HeaderSubtitle>
        </Container>
      </Header>

      <Section>
        <Container>
          <SectionTitle>Why Digital Marketing is Essential for Doctors</SectionTitle>
          <p style={{textAlign: 'center', fontSize: '1.2rem', color: '#666', maxWidth: '800px', margin: '0 auto'}}>
            In today's digital age, 80% of patients search online for healthcare providers before making an appointment. 
            A strong digital presence is no longer optional—it's essential for the growth and success of your medical practice.
          </p>
          
          <BenefitsGrid>
            <BenefitCard>
              <BenefitIcon><FaUserMd /></BenefitIcon>
              <BenefitTitle>Increased Patient Acquisition</BenefitTitle>
              <BenefitDescription>
                Reach potential patients actively searching for healthcare services in your area. 
                Convert online searches into booked appointments with targeted marketing strategies.
              </BenefitDescription>
            </BenefitCard>
            
            <BenefitCard>
              <BenefitIcon><FaChartLine /></BenefitIcon>
              <BenefitTitle>Build Trust & Credibility</BenefitTitle>
              <BenefitDescription>
                Showcase your expertise through educational content, patient testimonials, 
                and professional online presence. Establish yourself as a trusted authority in your specialty.
              </BenefitDescription>
            </BenefitCard>
            
            <BenefitCard>
              <BenefitIcon><FaMobileAlt /></BenefitIcon>
              <BenefitTitle>24/7 Practice Visibility</BenefitTitle>
              <BenefitDescription>
                Your digital presence works around the clock. Patients can find your services, 
                read reviews, and schedule appointments anytime, even outside office hours.
              </BenefitDescription>
            </BenefitCard>
          </BenefitsGrid>
        </Container>
      </Section>

      <Section style={{backgroundColor: '#f8f9fa'}}>
        <Container>
          <SectionTitle>Comprehensive Digital Marketing Services for Healthcare</SectionTitle>
          <p style={{textAlign: 'center', color: '#666', marginBottom: '50px', maxWidth: '800px', margin: '0 auto 50px'}}>
            We offer specialized digital marketing services designed specifically for healthcare providers 
            to help you reach more patients and grow your practice.
          </p>
          
          <ServicesGrid>
            <ServiceCard>
              <ServiceIcon><FaSearch /></ServiceIcon>
              <ServiceContent>
                <ServiceTitle>Medical SEO (Search Engine Optimization)</ServiceTitle>
                <ServiceDescription>
                  Optimize your website to rank higher in search results for medical services, 
                  conditions, and treatments. Target local patients searching for healthcare providers in your area.
                </ServiceDescription>
              </ServiceContent>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon><FaMapMarkerAlt /></ServiceIcon>
              <ServiceContent>
                <ServiceTitle>Local SEO for Healthcare Practices</ServiceTitle>
                <ServiceDescription>
                  Improve your visibility in local search results, Google Maps, and directory listings. 
                  Essential for attracting patients from your immediate geographic area.
                </ServiceDescription>
              </ServiceContent>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon><FaStar /></ServiceIcon>
              <ServiceContent>
                <ServiceTitle>Reputation Management</ServiceTitle>
                <ServiceDescription>
                  Monitor and manage online reviews, respond to patient feedback, and build a positive online reputation 
                  that attracts new patients to your practice.
                </ServiceDescription>
              </ServiceContent>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon><FaNewspaper /></ServiceIcon>
              <ServiceContent>
                <ServiceTitle>Content Marketing for Healthcare</ServiceTitle>
                <ServiceDescription>
                  Create educational content, blog posts, articles, and health guides that establish your expertise 
                  and help patients find answers to their health questions.
                </ServiceDescription>
              </ServiceContent>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon><FaVideo /></ServiceIcon>
              <ServiceContent>
                <ServiceTitle>Medical Video Marketing</ServiceTitle>
                <ServiceDescription>
                  Produce educational videos, procedure explanations, and practice introductions that engage patients 
                  and build trust through visual content.
                </ServiceDescription>
              </ServiceContent>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon><FaEnvelope /></ServiceIcon>
              <ServiceContent>
                <ServiceTitle>Healthcare Email Marketing</ServiceTitle>
                <ServiceDescription>
                  Stay connected with current patients through newsletters, health tips, appointment reminders, 
                  and practice updates while maintaining HIPAA compliance.
                </ServiceDescription>
              </ServiceContent>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon><FaComments /></ServiceIcon>
              <ServiceContent>
                <ServiceTitle>Social Media Management for Healthcare</ServiceTitle>
                <ServiceDescription>
                  Build your presence on platforms where patients spend time. Share health tips, practice updates, 
                  and engage with your community in a professional manner.
                </ServiceDescription>
              </ServiceContent>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon><FaShieldAlt /></ServiceIcon>
              <ServiceContent>
                <ServiceTitle>PPC Advertising for Healthcare</ServiceTitle>
                <ServiceDescription>
                  Run targeted pay-per-click campaigns on Google and social media to reach patients 
                  actively searching for your specific medical services.
                </ServiceDescription>
              </ServiceContent>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon><FaCalendarCheck /></ServiceIcon>
              <ServiceContent>
                <ServiceTitle>Website Design & Optimization</ServiceTitle>
                <ServiceDescription>
                  Create patient-friendly websites with online appointment scheduling, telemedicine integration, 
                  and mobile-responsive design that converts visitors into patients.
                </ServiceDescription>
              </ServiceContent>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon><FaUserMd /></ServiceIcon>
              <ServiceContent>
                <ServiceTitle>Medical Practice Branding</ServiceTitle>
                <ServiceDescription>
                  Develop a strong brand identity that communicates your expertise, values, and unique approach to patient care.
                </ServiceDescription>
              </ServiceContent>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon><FaChartLine /></ServiceIcon>
              <ServiceContent>
                <ServiceTitle>Analytics & Performance Tracking</ServiceTitle>
                <ServiceDescription>
                  Monitor your marketing ROI with detailed analytics that track patient inquiries, appointments, 
                  and practice growth metrics.
                </ServiceDescription>
              </ServiceContent>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon><FaMobileAlt /></ServiceIcon>
              <ServiceContent>
                <ServiceTitle>Mobile Marketing for Healthcare</ServiceTitle>
                <ServiceDescription>
                  Reach patients on their mobile devices with optimized content, SMS reminders, 
                  and mobile-friendly marketing strategies.
                </ServiceDescription>
              </ServiceContent>
            </ServiceCard>
          </ServicesGrid>
        </Container>
      </Section>

      <Section style={{backgroundColor: '#f8f9fa'}}>
        <Container>
          <SectionTitle>Proven Results for Healthcare Practices</SectionTitle>
          <StatsSection>
            <StatCard>
              <StatNumber>72%</StatNumber>
              <StatLabel>Increase in New Patient Appointments</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>45%</StatNumber>
              <StatLabel>Reduction in Patient Acquisition Cost</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>89%</StatNumber>
              <StatLabel>of Patients Use Online Reviews</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>3.5x</StatNumber>
              <StatLabel>Higher Patient Engagement</StatLabel>
            </StatCard>
          </StatsSection>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionTitle>Our Healthcare Marketing Packages</SectionTitle>
          <p style={{textAlign: 'center', color: '#666', marginBottom: '50px'}}>
            Choose the perfect package for your practice. All plans include healthcare-specific strategies.
          </p>
          
          <PricingSection>
            <PricingCard>
              <PlanName>Starter Package</PlanName>
              <PlanPrice>₹299<span>/month</span></PlanPrice>
              <FeatureList>
                <FeatureItem>Google Business Profile Optimization</FeatureItem>
                <FeatureItem>Basic Website SEO</FeatureItem>
                <FeatureItem>Social Media Setup</FeatureItem>
                <FeatureItem>Monthly Performance Report</FeatureItem>
                <FeatureItem>Email Support</FeatureItem>
              </FeatureList>
              <CTAButton>Get Started</CTAButton>
            </PricingCard>
            
            <PricingCard featured>
              <PopularBadge>MOST POPULAR</PopularBadge>
              <PlanName>Professional Package</PlanName>
              <PlanPrice>₹699<span>/month</span></PlanPrice>
              <FeatureList>
                <FeatureItem>Everything in Starter</FeatureItem>
                <FeatureItem>Advanced Local SEO</FeatureItem>
                <FeatureItem>Google Ads Management</FeatureItem>
                <FeatureItem>Social Media Marketing</FeatureItem>
                <FeatureItem>Patient Review Management</FeatureItem>
                <FeatureItem>Monthly Content Creation</FeatureItem>
                <FeatureItem>Priority Support</FeatureItem>
              </FeatureList>
              <CTAButton primary>Get Started</CTAButton>
            </PricingCard>
            
            <PricingCard>
              <PlanName>Enterprise Package</PlanName>
              <PlanPrice>₹1,299<span>/month</span></PlanPrice>
              <FeatureList>
                <FeatureItem>Everything in Professional</FeatureItem>
                <FeatureItem>Comprehensive Digital Strategy</FeatureItem>
                <FeatureItem>Video Marketing</FeatureItem>
                <FeatureItem>Reputation Management</FeatureItem>
                <FeatureItem>Competitor Analysis</FeatureItem>
                <FeatureItem>Dedicated Account Manager</FeatureItem>
                <FeatureItem>24/7 Premium Support</FeatureItem>
              </FeatureList>
              <CTAButton>Contact Sales</CTAButton>
            </PricingCard>
          </PricingSection>
        </Container>
      </Section>

      <TestimonialSection>
        <Container>
          <SectionTitle style={{color: 'white'}}>Success Stories</SectionTitle>
          <Testimonial>
            "After implementing their digital marketing strategy, our clinic saw a 60% increase in new patient appointments within 3 months. The targeted approach for healthcare professionals made all the difference."
          </Testimonial>
          <DoctorName>Dr. Sarah Johnson, Cardiology Specialist</DoctorName>
        </Container>
      </TestimonialSection>

      <Section style={{backgroundColor: '#f8f9fa'}}>
        <Container>
          <SectionTitle>Healthcare-Specific Features</SectionTitle>
          <BenefitsGrid>
            <BenefitCard>
              <BenefitIcon><FaSearch /></BenefitIcon>
              <BenefitTitle>HIPAA Compliant Solutions</BenefitTitle>
              <BenefitDescription>
                All our marketing strategies are designed with healthcare compliance in mind, 
                ensuring patient data protection and privacy regulations are met.
              </BenefitDescription>
            </BenefitCard>
            
            <BenefitCard>
              <BenefitIcon><FaStar /></BenefitIcon>
              <BenefitTitle>Review & Reputation Management</BenefitTitle>
              <BenefitDescription>
                Manage your online reputation with tools designed specifically for healthcare providers. 
                Encourage positive reviews and respond professionally to feedback.
              </BenefitDescription>
            </BenefitCard>
            
            <BenefitCard>
              <BenefitIcon><FaShieldAlt /></BenefitIcon>
              <BenefitTitle>Medical SEO Specialists</BenefitTitle>
              <BenefitDescription>
                Our team includes SEO experts who understand healthcare terminology, 
                patient search behavior, and medical practice requirements.
              </BenefitDescription>
            </BenefitCard>
          </BenefitsGrid>
        </Container>
      </Section>

     
    </Home>
  );
};

export default DigitalMarketing;